/**
 * Implement this interface if your component has to support container breakpoints.
 * @example See the Card Teaser component
 */
export interface ContainerBreakpointComponent {
  /**
   * The host element of the custom component. Should be annotated with the Stencil decorator @Element().
   * @example @Element() hostElement: HTMLEonUiMyComponentElement;
   */
  hostElement;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints: number[];

  /**
   * Initializes the container breakpoint function
   *
   * @default
   * initContainerBreakpoints() {
   *   initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
   * }
   */
  initContainerBreakpoints(): void;
}

/**
 * attaches specific container breakpoint classes to host element which are based on the component width.
 * As container queries are not yet ready to be used in all browsers and in combination with web components this feature is provided via resize observer.
 * Only disadvantage is, that there is only one global css class on host element level. Using container queries within scss strcture is not possible.
 * @param component
 * @param breakpoints
 */
export function initContainerBreakpointsDelegate(component: any, breakpoints: number[]) {
  // sort container breakpoints in ascending order for comparison
  breakpoints = breakpoints.sort((a, b) => a - b);

  // resize observer for tracking component width
  var observer = new window.ResizeObserver((entries) => {
    // if component width is higher than largest breakpoint remove class and return
    if (entries[0].contentRect.width > breakpoints[breakpoints.length - 1]) {
      for (let breakpoint of breakpoints) {
        component.classList.remove(`container-breakpoint-${breakpoint}`);
        component.classList.add(`container-breakpoint-largest`);
        // check if component has a setContainerBreakpoint method and call it
        if (typeof component.setContainerBreakpoint === "function") {
          component.setContainerBreakpoint("largest");
        }
      }
      return;
    }

    // go trough all container breakpoints until one matches
    for (let breakpoint of breakpoints) {
      if (entries[0].contentRect.width <= breakpoint) {
        // check if container breakpoint class is already attached. If so, no update is needed.
        if (!component.classList.contains(`container-breakpoint-${breakpoint}`)) {
          // remove all attached container breakpoint classes
          for (let breakpoint of breakpoints) {
            component.classList.remove(`container-breakpoint-${breakpoint}`);
            component.classList.remove(`container-breakpoint-largest`);
          }
          // add matching container breakpoint class
          component.classList.add(`container-breakpoint-${breakpoint}`);
          // check if component has a setContainerBreakpoint method and call it
          // the components headline && headline-brand uses this method to react to the container breakpoint change
          if (typeof component.setContainerBreakpoint === "function") {
            component.setContainerBreakpoint(breakpoint);
          }
        }
        // force loop to stop
        break;
      }
    }
  });

  observer.observe(component);
}
